import { FC, useState } from "react";
import styles from './styles.module.scss';
import { getDocumentUrl } from '../../../shared/config/api';
import { useMobileMenuContext } from '../../../shared/contexts/mobile-menu-context';
import { FORMAT_LABELS, TYPE_LABELS } from 'widgets/courses/types';
import { useWebsiteCatalog } from 'entities/course-catalog';

type SectionType = 'basic-info' | 'structure' | 'documents' | 'education' | 'standards' | 'staff' | 'material-support' | 'scholarships' | 'paid-services' | 'financial' | 'vacant-places' | 'accessibility' | 'international';

// Скрытые блоки. Файлы остаются на сервере, разметка — ниже под флагом: вернуть = поставить true.
const SHOW_LOCAL_REGULATIONS = false;   // «Документы» → Локальные нормативные акты
const SHOW_ORDERS = false;              // «Документы» → Приказы
const SHOW_DOCUMENT_FORMS = false;      // «Документы» → Формы документов
const SHOW_STUDENTS_COUNT = false;      // «Образование» → Численность обучающихся

const EDUCATIONAL_PROGRAM_FILE = "2026.06.01 - ОБРАЗОВАТЕЛЬНАЯ программа ИП Гелдабаев Ю.И..pdf";
const OFFER_CONTRACT_FILE = "ред_12_08_2026_Договор_публичная_оферта_ИП_Гелдабаев_Ю_И.pdf";
const OFFER_APPROVAL_ORDER_FILE = "приказ_об_утверждении_оферты_на_ИП_Гелдабаев.pdf";
const LICENSE_FILE = "Реестровая выписка.pdf";

export const EducationInfo: FC = () => {
    const [activeSection, setActiveSection] = useState<SectionType>('basic-info');
    const { isMobileMenuOpen, isMobile, closeMobileMenu } = useMobileMenuContext();
    const { status: catalogStatus, catalog, error: catalogError, retry: retryCatalog } = useWebsiteCatalog();

    const handleSectionChange = (sectionId: SectionType) => {
        setActiveSection(sectionId);
        if (isMobile) {
            closeMobileMenu();
        }
    };

    const sections = [
        {
            id: 'basic-info' as SectionType,
            title: 'Основные сведения',
            description: 'Общая информация об организации'
        },
        {
            id: 'structure' as SectionType,
            title: 'Структура и органы управления',
            description: 'Руководство и подразделения'
        },
        {
            id: 'documents' as SectionType,
            title: 'Документы',
            description: 'Уставные и локальные документы'
        },
        {
            id: 'education' as SectionType,
            title: 'Образование',
            description: 'Образовательные программы'
        },
        {
            id: 'standards' as SectionType,
            title: 'Образовательные стандарты',
            description: 'ФГОС и требования'
        },
        {
            id: 'staff' as SectionType,
            title: 'Руководство. Педагогический состав',
            description: 'Информация о сотрудниках'
        },
        {
            id: 'material-support' as SectionType,
            title: 'Материально-техническое обеспечение',
            description: 'Оснащенность образовательного процесса'
        },
        {
            id: 'scholarships' as SectionType,
            title: 'Стипендии и меры поддержки',
            description: 'Социальная поддержка обучающихся'
        },
        {
            id: 'paid-services' as SectionType,
            title: 'Платные образовательные услуги',
            description: 'Порядок оказания и стоимость'
        },
        {
            id: 'financial' as SectionType,
            title: 'Финансово-хозяйственная деятельность',
            description: 'Финансовые показатели'
        },
        {
            id: 'vacant-places' as SectionType,
            title: 'Вакантные места',
            description: 'Места для приема обучающихся'
        },
        {
            id: 'accessibility' as SectionType,
            title: 'Доступная среда',
            description: 'Условия для инвалидов и лиц с ОВЗ'
        },
        {
            id: 'international' as SectionType,
            title: 'Международное сотрудничество',
            description: 'Договоры с зарубежными организациями'
        }
    ];

    const renderSectionContent = (sectionId: SectionType) => {
        switch (sectionId) {
            case 'basic-info':
                return (
                    <div className={styles.contentSection}>
                        <h2>Основные сведения</h2>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Полное наименование:</strong>
                                <span>Индивидуальный предприниматель Гелдабаев Юнус Исхакович</span>
                            </div>

                            <div className={styles.infoItem}>
                                <strong>Дата создания:</strong>
                                <span>2025 год</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Место нахождения:</strong>
                                <span>Чеченская Республика, г. Грозный, ул. Сайханова, зд. 29А</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Режим работы:</strong>
                                <span>Понедельник - воскресенье: 9:00 - 18:00</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Контактный телефон:</strong>
                                <span> +7 995 333 4456</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Электронная почта:</strong>
                                <span>yunus.geldabaev@gmail.com</span>
                            </div>
                        </div>
                    </div>
                );

            case 'structure':
                return (
                    <div className={styles.contentSection}>
                        <h2>Структура и органы управления образовательной организацией</h2>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Руководитель:</strong>
                                <span>Гелдабаев Юнус Исхакович</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Место нахождения:</strong>
                                <span>Чеченская Республика, г. Грозный, ул. Сайханова, зд. 29А</span>
                            </div>
                        </div>
                    </div>
                );

            case 'documents':
                return (
                    <div className={styles.contentSection}>
                        <h2>Документы</h2>
                        <div className={styles.contractOffer}>
                            <strong>Платные образовательные услуги:</strong>{' '}
                            <a href={getDocumentUrl(OFFER_CONTRACT_FILE)} target="_blank" rel="noopener noreferrer">Договор-оферта</a>
                            {' · '}
                            <a href={getDocumentUrl(OFFER_APPROVAL_ORDER_FILE)} target="_blank" rel="noopener noreferrer">
                                Приказ № 5 об утверждении оферты
                            </a>
                        </div>
                        <div className={styles.documentsList}>
                            {SHOW_LOCAL_REGULATIONS && (
                                <div className={styles.docCategory}>
                                    <h3>Локальные нормативные акты</h3>
                                    <ul>
                                        <li><a href={getDocumentUrl("Правила внутреннего  распорядка Обучающихся.pdf")} target="_blank" rel="noopener noreferrer">📄 Правила внутреннего распорядка обучающихся (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Правила внутреннего трудового распорядка.pdf")} target="_blank" rel="noopener noreferrer">📄 Правила внутреннего трудового распорядка (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Положение о порядке приема и отчисления .pdf")} target="_blank" rel="noopener noreferrer">📄 Положение о порядке приема и отчисления (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Положение о режиме занятий обучающихся.pdf")} target="_blank" rel="noopener noreferrer">📄 Положение о режиме занятий обучающихся (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Положение о текущем контроле успеваемости и итоовой аттестации .pdf")} target="_blank" rel="noopener noreferrer">📄 Положение о текущем контроле успеваемости и итоговой аттестации (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Положение о порядке оформления и выдачи документов.pdf")} target="_blank" rel="noopener noreferrer">📄 Положение о порядке оформления и выдачи документов (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Положение об охране здоровья.pdf")} target="_blank" rel="noopener noreferrer">📄 Положение об охране здоровья (PDF)</a></li>
                                        <li><a href={getDocumentUrl("положение об обработке персональных данных.pdf")} target="_blank" rel="noopener noreferrer">📄 Положение об обработке персональных данных (PDF)</a></li>
                                    </ul>
                                </div>
                            )}

                            {SHOW_ORDERS && (
                                <div className={styles.docCategory}>
                                    <h3>Приказы</h3>
                                    <ul>
                                        <li><a href={getDocumentUrl("Приказ о приеме на обучение.pdf")} target="_blank" rel="noopener noreferrer">📄 Приказ о приеме на обучение (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Приказ об утверждении стоимости программ.pdf")} target="_blank" rel="noopener noreferrer">📄 Приказ об утверждении стоимости программ (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Приказ о назначении ответственного за обработку персональных данных.pdf")} target="_blank" rel="noopener noreferrer">📄 Приказ о назначении ответственного за обработку персональных данных (PDF)</a></li>
                                        <li><a href={getDocumentUrl("2026.06.01 - ПРИКАЗ № 4 Об утверждении образ.программы ИП Гелдабаев Ю.И..pdf")} target="_blank" rel="noopener noreferrer">📄 Приказ № 4 об утверждении образовательной программы (PDF)</a></li>
                                        <li><a href={getDocumentUrl(OFFER_APPROVAL_ORDER_FILE)} target="_blank" rel="noopener noreferrer">📄 Приказ № 5 об утверждении правил оказания услуг и введении в действие публичной оферты (PDF)</a></li>
                                    </ul>
                                </div>
                            )}

                            {SHOW_DOCUMENT_FORMS && (
                                <div className={styles.docCategory}>
                                    <h3>Формы документов</h3>
                                    <ul>
                                        <li><a href={getDocumentUrl("Заявление о приеме на обучение.pdf")} target="_blank" rel="noopener noreferrer">📄 Заявление о приеме на обучение (PDF)</a></li>
                                        <li><a href={getDocumentUrl("Форма_согласия на обработку персональных данных.pdf")} target="_blank" rel="noopener noreferrer">📄 Форма согласия на обработку персональных данных (PDF)</a></li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'education':
                return (
                    <div className={styles.contentSection}>
                        <h2>Образование</h2>
                        <div className={styles.programDocuments}>
                            <h3>Образовательная программа</h3>
                            <ul>
                                <li><a href={getDocumentUrl(EDUCATIONAL_PROGRAM_FILE)} target="_blank" rel="noopener noreferrer">📄 Образовательная программа ИП Гелдабаев Ю.И. (PDF)</a></li>
                                <li><a href={getDocumentUrl("2026.06.01 - ПРИКАЗ № 4 Об утверждении образ.программы ИП Гелдабаев Ю.И..pdf")} target="_blank" rel="noopener noreferrer">📄 Приказ № 4 об утверждении образовательной программы (PDF)</a></li>
                            </ul>
                        </div>
                        <div className={styles.educationPrograms}>
                            <div className={styles.program}>
                                <h3>Python Погружение</h3>
                                <p>Базовый курс для изучения основ программирования на Python. Подходит для начинающих без опыта в программировании.</p>
                                <div className={styles.programInfo}>
                                    <p><strong>Продолжительность:</strong> 6 месяцев</p>
                                    <p><strong>Формат:</strong> Онлайн и оффлайн обучение</p>
                                    <p><strong>Язык обучения:</strong> Русский</p>
                                    <p><strong>Электронное обучение:</strong> Используется</p>
                                </div>
                            </div>
                            
                            <div className={styles.program}>
                                <h3>Python Восхождение</h3>
                                <p>Продвинутый курс Python для тех, кто уже знаком с основами программирования. Углубленное изучение языка и его возможностей.</p>
                                <div className={styles.programInfo}>
                                    <p><strong>Продолжительность:</strong> 6 месяцев</p>
                                    <p><strong>Формат:</strong> Онлайн и оффлайн обучение</p>
                                    <p><strong>Язык обучения:</strong> Русский</p>
                                    <p><strong>Электронное обучение:</strong> Используется</p>
                                </div>
                            </div>
                            
                            <div className={styles.program}>
                                <h3>Python Эксперт</h3>
                                <p>Экспертный уровень Python разработки. Изучение сложных концепций, архитектурных решений и профессиональных практик.</p>
                                <div className={styles.programInfo}>
                                    <p><strong>Продолжительность:</strong> 5 месяцев</p>
                                    <p><strong>Формат:</strong> Онлайн и оффлайн обучение</p>
                                    <p><strong>Язык обучения:</strong> Русский</p>
                                    <p><strong>Электронное обучение:</strong> Используется</p>
                                </div>
                            </div>
                            
                            <div className={styles.program}>
                                <h3>Фронтенд Старт</h3>
                                <p>Комплексный курс по фронтенд разработке. Изучение HTML, CSS, JavaScript и современных фреймворков для создания веб-приложений.</p>
                                <div className={styles.programInfo}>
                                    <p><strong>Продолжительность:</strong> 6 месяцев</p>
                                    <p><strong>Формат:</strong> Онлайн и оффлайн обучение</p>
                                    <p><strong>Язык обучения:</strong> Русский</p>
                                    <p><strong>Электронное обучение:</strong> Используется</p>
                                </div>
                            </div>
                        </div>
                        
                        {SHOW_STUDENTS_COUNT && (
                            <div className={styles.studentsCount}>
                                <h3>Численность обучающихся</h3>
                                <div className={styles.infoGrid}>
                                    <div className={styles.infoItem}>
                                        <strong>За счет бюджетных ассигнований:</strong>
                                        <span>0 человек</span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <strong>По договорам об оказании платных образовательных услуг:</strong>
                                        <span>Информация уточняется</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={styles.studentsCount}>
                            <h3>Сведения о реализации образовательных программ</h3>
                            <ul className={styles.plainList}>
                                <li>Язык реализации программ — государственный язык Российской Федерации — русский.</li>
                                <li>Учебный процесс осуществляется непрерывно в течение всего календарного года.</li>
                                <li>Общая численность обучающихся по реализуемым программам не ограничена.</li>
                                <li>Обучение проводится в очной, очно-заочной и заочной формах обучения. Форма обучения указывается в соответствующей образовательной программе.</li>
                                <li>Время, место, формат проведения занятий определяются расписанием в соответствующей образовательной программе.</li>
                            </ul>
                        </div>

                        <div className={styles.licenseInfo}>
                            <h3>Лицензия на осуществление образовательной деятельности</h3>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <strong>Номер лицензии:</strong>
                                    <span><a className={styles.licenseLink} href={getDocumentUrl(LICENSE_FILE)} target="_blank" rel="noopener noreferrer">№ Л035-01275-20/01749408</a></span>
                                </div>
                                <div className={styles.infoItem}>
                                    <strong>Дата выдачи:</strong>
                                    <span>20.01.2025</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <strong>Орган, выдавший лицензию:</strong>
                                    <span>Министерство образования и науки Чеченской Республики</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'standards':
                return (
                    <div className={styles.contentSection}>
                        <h2>Образовательные стандарты и требования</h2>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Нормативная база:</strong>
                                <span>
                                    Образовательные программы разработаны с учетом Федерального закона "Об образовании в Российской Федерации" от 29.12.2012 N 273-ФЗ 
                                    и Приказа Министерства просвещения РФ от 27 июля 2022 г. N 629 "Об утверждении Порядка организации и осуществления образовательной деятельности по дополнительным общеобразовательным программам".
                                    <br/><br/>
                                    <a href="http://www.consultant.ru/document/cons_doc_LAW_140174/" target="_blank" rel="noopener noreferrer" style={{color: '#5B21CC', textDecoration: 'underline'}}>Ссылка на Федеральный закон "Об образовании в Российской Федерации"</a>
                                </span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Применяемые стандарты:</strong>
                                <span>Образовательные программы разработаны в соответствии с профессиональными стандартами в области информационных технологий</span>
                            </div>
                        </div>
                    </div>
                );

            case 'staff':
                return (
                    <div className={styles.contentSection}>
                        <h2>Руководство. Педагогический (научно-педагогический) состав</h2>
                        
                        <div className={styles.staffSection}>
                            <h3>Руководитель образовательной организации</h3>
                            <div className={styles.staffMember}>
                                <div className={styles.staffInfo}>
                                    <p><strong>ФИО:</strong> Гелдабаев Юнус Исхакович</p>
                                    <p><strong>Должность:</strong> Индивидуальный предприниматель, директор</p>
                                    <p><strong>Контактный телефон:</strong> +7 995 333 4456</p>
                                    <p><strong>Электронная почта:</strong> yunus.geldabaev@gmail.com</p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.staffSection}>
                            <h3>Педагогический состав</h3>
                            <div className={styles.teachersList}>
                                <div className={styles.teacher}>
                                    <h4>Гелдабаев Юнус Исхакович</h4>
                                    <div className={styles.teacherInfo}>
                                        <p><strong>Должность:</strong> Преподаватель Python, Senior Manager Of Artificial Intelligence</p>
                                        <p><strong>Уровень образования:</strong> Высшее</p>
                                        <p><strong>Квалификация:</strong> Инженер-программист</p>
                                        <p><strong>Направление подготовки:</strong> Информатика и вычислительная техника</p>
                                        <p><strong>Общий стаж работы:</strong> 8 лет</p>
                                        <p><strong>Стаж работы по специальности:</strong> 8 лет</p>
                                        <p><strong>Преподаваемые дисциплины:</strong> Python программирование, Алгоритмы и структуры данных, Машинное обучение</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'material-support':
                return (
                    <div className={styles.contentSection}>
                        <h2>Материально-техническое обеспечение и оснащенность образовательного процесса</h2>
                        
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Формат обучения:</strong>
                                <span>
                                    Организация осуществляет обучение в очном формате — студенты посещают занятия в школе, а также в онлайн-формате. Обеспечиваются все необходимые условия для осуществления образовательного процесса в обоих форматах.
                                </span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Оборудованные учебные кабинеты:</strong>
                                <span>Информация уточняется</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Объекты для проведения практических занятий:</strong>
                                <span>Обучение проводится в онлайн и оффлайн форматах с использованием современных технологий</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Средства обучения и воспитания:</strong>
                                <span>Компьютерная техника, лицензионное программное обеспечение для разработки</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Доступ к информационным системам:</strong>
                                <span>Обеспечен высокоскоростной доступ к сети Интернет, образовательным платформам</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Электронные образовательные ресурсы:</strong>
                                <span>Используются современные онлайн-платформы и инструменты для обучения программированию</span>
                            </div>
                        </div>
                    </div>
                );

            case 'scholarships':
                return (
                    <div className={styles.contentSection}>
                        <h2>Стипендии и меры поддержки обучающихся</h2>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Стипендии:</strong>
                                <span>Не предоставляются</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Меры социальной поддержки:</strong>
                                <span>Возможность получения налогового вычета за обучение</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Общежитие:</strong>
                                <span>Не предоставляется</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Содействие в трудоустройстве:</strong>
                                <span>Оказывается помощь в составлении резюме и подготовке к собеседованиям</span>
                            </div>
                        </div>
                        
                        <div className={styles.taxDeduction}>
                            <h3>Документы для получения налогового вычета</h3>
                            <ul>
                                <li><a href={getDocumentUrl("форма справки вычет 2024-2025.pdf")} target="_blank" rel="noopener noreferrer">📄 Форма справки для налогового вычета (PDF)</a></li>
                                <li><a href={getDocumentUrl("Комментарии по заполнения для ученика.docx")} target="_blank" rel="noopener noreferrer">📄 Комментарии по заполнению для ученика (DOCX)</a></li>
                                <li><a href={getDocumentUrl("ОБРАЗЕЦ СПРАВКИ 1.jpg")} target="_blank" rel="noopener noreferrer">🖼️ Образец справки (страница 1) (JPG)</a></li>
                                <li><a href={getDocumentUrl("ОБРАЗЕЦ СПРАВКИ 2.jpg")} target="_blank" rel="noopener noreferrer">🖼️ Образец справки (страница 2) (JPG)</a></li>
                            </ul>
                        </div>
                    </div>
                );

            case 'paid-services':
                return (
                    <div className={styles.contentSection}>
                        <h2>Платные образовательные услуги</h2>

                        <p className={styles.leadText}>
                            Условия оказания ИП Гелдабаев Юнус Исхакович платных образовательных услуг и стоимость обучения
                            по каждой образовательной программе содержатся в Договоре-оферте на оказание платных
                            образовательных услуг:{' '}
                            <a href={getDocumentUrl(OFFER_CONTRACT_FILE)} target="_blank" rel="noopener noreferrer">Договор-Оферта</a>,
                            или иных договорах, заключаемых между Исполнителем и Слушателем и/или Заказчиком.
                        </p>

                        <div className={styles.paidServices}>
                            <div className={styles.serviceInfo}>
                                <h3>Порядок оказания платных образовательных услуг</h3>
                                <p>Образовательные услуги оказываются на основании договора об оказании платных образовательных услуг</p>
                                <a href={getDocumentUrl(OFFER_CONTRACT_FILE)} target="_blank" rel="noopener noreferrer">
                                    📄 Образец договора об оказании платных образовательных услуг (PDF)
                                </a>
                                <a href={getDocumentUrl(OFFER_APPROVAL_ORDER_FILE)} target="_blank" rel="noopener noreferrer">
                                    📄 Приказ № 5 об утверждении договора-оферты (PDF)
                                </a>
                            </div>
                            
                            <div className={styles.priceList}>
                                <h3>Стоимость обучения по образовательным программам</h3>
                                <div className={styles.priceTable}>
                                    {catalogStatus === 'loading' ? <span role="status">Загружаем цены…</span> : null}
                                    {catalogStatus === 'error' ? (
                                        <span role="alert">{catalogError} <button type="button" onClick={retryCatalog}>Повторить</button></span>
                                    ) : null}
                                    {catalog?.courses.map((course) => (
                                        <div className={styles.priceRow} key={course.id}>
                                            <span className={styles.courseName}>
                                                {course.title}{course.trainingType ? ` — ${course.trainingType}` : ''} · {course.duration}
                                            </span>
                                            <div className={styles.prices}>
                                                {catalogStatus === 'ready' ? (['online', 'offline'] as const).flatMap((format) =>
                                                    (['group', 'closed_group', 'individual'] as const).map((type) => {
                                                        const option = course.pricing.find(p => p.format === format && p.type === type);
                                                        if (!option) return null;
                                                        const suffix = course.billingUnit === 'course' ? '₽' : '₽/мес';
                                                        return (
                                                            <span key={`${format}-${type}`}>
                                                                {FORMAT_LABELS[format]} {TYPE_LABELS[type].toLowerCase()}: {option.price.toLocaleString()} {suffix}
                                                            </span>
                                                        );
                                                    })
                                                ) : <span>Не удалось загрузить цены</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'financial':
                return (
                    <div className={styles.contentSection}>
                        <h2>Финансово-хозяйственная деятельность</h2>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Финансовое обеспечение образовательной деятельности:</strong>
                                <span>Осуществляется за счет средств физических лиц по договорам об оказании платных образовательных услуг</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Бюджетное финансирование:</strong>
                                <span>Не осуществляется</span>
                            </div>
                        </div>
                    </div>
                );

            case 'vacant-places':
                return (
                    <div className={styles.contentSection}>
                        <h2>Вакантные места для приема (перевода) обучающихся</h2>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Информация о вакантных местах:</strong>
                                <span>Количество вакантных мест определяется в зависимости от набора групп и формируется по мере поступления заявок от обучающихся</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Прием на обучение:</strong>
                                <span>Осуществляется в течение всего календарного года по мере формирования групп</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Актуальная информация:</strong>
                                <span>Для получения актуальной информации о наличии свободных мест обращайтесь по телефону +7 995 333 4456 или email: yunus.geldabaev@gmail.com</span>
                            </div>
                        </div>
                    </div>
                );

            case 'accessibility':
                return (
                    <div className={styles.contentSection}>
                        <h2>Доступная среда</h2>
                        <ol className={styles.numberedList}>
                            <li>
                                Образовательные программы ИП Гелдабаев Юнус Исхакович (Школа программирования «Ансар»)
                                адаптированы для прохождения обучения инвалидами и лицами с ограниченными возможностями
                                здоровья (ОВЗ) с применением электронного обучения и дистанционных образовательных
                                технологий. Данный формат позволяет обеспечить беспрепятственный доступ к образовательным
                                услугам для маломобильных групп населения без необходимости физического посещения учебных
                                помещений.
                            </li>
                            <li>
                                При реализации образовательных программ используются современные информационные технологии,
                                технические средства, а также информационно-телекоммуникационные сети, обеспечивающие
                                передачу информации по линиям связи и полноценное взаимодействие обучающихся и
                                педагогического состава.
                            </li>
                            <li>
                                Электронная информационно-образовательная среда Школы программирования «Ансар» обеспечивает
                                освоение обучающимися образовательных программ в полном объеме независимо от их места
                                нахождения и состояния здоровья.
                            </li>
                            <li>
                                Дистанционное обучение реализуется на специализированной образовательной платформе
                                (электронном образовательном ресурсе):{' '}
                                <a href="https://ansarium.ru/" target="_blank" rel="noopener noreferrer">https://ansarium.ru/</a>
                            </li>
                            <li>
                                Обучающийся получает полный доступ к электронным материалам курса (текст курса, презентации,
                                видеолекции, методические материалы и т.п.) после оплаты его стоимости.
                            </li>
                            <li>
                                Помещения для проведения занятий, в том числе с использованием дистанционных образовательных
                                технологий, подключены к сети интернет. Имеется дополнительное оборудование для проведения
                                видеоконференций и занятий в дистанционном режиме.
                            </li>
                        </ol>
                    </div>
                );

            case 'international':
                return (
                    <div className={styles.contentSection}>
                        <h2>Международное сотрудничество</h2>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <strong>Договоры с иностранными организациями:</strong>
                                <span>В настоящее время отсутствуют</span>
                            </div>
                            <div className={styles.infoItem}>
                                <strong>Международная аккредитация:</strong>
                                <span>В настоящее время отсутствует</span>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className={styles.educationInfo} itemScope itemType="https://schema.org/EducationalOrganization">
            {/* JSON-LD структурированные данные */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "EducationalOrganization",
                    "name": "Школа программирования Ансар",
                    "legalName": "Индивидуальный предприниматель Гелдабаев Юнус Исхакович",
                    "alternateName": "ИП Гелдабаев Ю.И.",
                    "url": "https://itansar.ru",
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "ул. Сайханова, зд. 29А",
                        "addressLocality": "Грозный",
                        "addressRegion": "Чеченская Республика",
                        "addressCountry": "RU"
                    },
                    "telephone": "+79953334456",
                    "email": "yunus.geldabaev@gmail.com",
                    "foundingDate": "2025",
                    "description": "Школа программирования, обучение Python и Frontend разработке",
                    "educationalCredentialAwarded": "Сертификат",
                    "hasCredential": "Лицензия на образовательную деятельность № Л035-01275-20/01749408"
                })
            }} />

            <div className={styles.__in}>
                <div className={styles.header}>
                    <h1>Сведения об образовательной организации</h1>
                    <p>Информация размещена в соответствии с требованиями законодательства РФ</p>
                </div>

                <div className={styles.layout}>
                    {/* Overlay для мобильного меню */}
                    {isMobile && isMobileMenuOpen && (
                        <div className={styles.mobileOverlay} onClick={closeMobileMenu}></div>
                    )}

                    {/* Боковое меню */}
                    <div 
                        className={`${styles.sidebar} ${isMobile && isMobileMenuOpen ? styles.mobileOpen : ''} ${isMobile && !isMobileMenuOpen ? styles.mobileHidden : ''}`}
                        data-mobile-menu
                    >
                        {/* Кнопка закрытия для мобильных */}
                        {isMobile && (
                            <div className={styles.mobileMenuHeader}>
                                <h3>Разделы</h3>
                                <button 
                                    className={styles.mobileCloseButton}
                                    onClick={closeMobileMenu}
                                    aria-label="Закрыть меню"
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                        
                        <nav className={styles.navigation}>
                            {sections.map((section) => (
                                <button
                                    key={section.id}
                                    className={`${styles.navItem} ${activeSection === section.id ? styles.active : ''}`}
                                    onClick={() => handleSectionChange(section.id)}
                                >
                                    <div className={styles.navItemContent}>
                                        <span className={styles.navTitle}>{section.title}</span>
                                        <span className={styles.navDescription}>{section.description}</span>
                                    </div>
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Основной контент */}
                    <div className={styles.content}>
                        {renderSectionContent(activeSection)}
                    </div>
                </div>
            </div>
        </div>
    );
};
